import type { LucideIcon } from 'lucide-react';
import {
  BookOpenCheck,
  BriefcaseBusiness,
  CalendarCheck,
  Cable,
  ClipboardCheck,
  FileSignature,
  Files,
  GraduationCap,
  Headphones,
  LayoutDashboard,
  Mail,
  ShieldCheck,
  Sparkles,
  Video,
} from 'lucide-react';

export type PortalNavigationGroupId = 'overview' | 'development' | 'documents' | 'setup' | 'help';

export interface PortalNavigationItem {
  id: string;
  label: string;
  shortLabel: string;
  path: string;
  description: string;
  icon: LucideIcon;
  group: PortalNavigationGroupId;
  mobilePrimary?: boolean;
  activePathPrefixes?: string[];
}

export const portalNavigationGroups: Array<{
  id: PortalNavigationGroupId;
  label: string;
}> = [
  { id: 'overview', label: 'Overview' },
  { id: 'development', label: 'Development' },
  { id: 'documents', label: 'Documents' },
  { id: 'setup', label: 'Setup' },
  { id: 'help', label: 'Help' },
];

export const portalNavigationItems: PortalNavigationItem[] = [
  {
    id: 'my-day',
    label: 'My day',
    shortLabel: 'Today',
    path: '/portal/my-day',
    description: 'Your current priorities and progress',
    icon: CalendarCheck,
    group: 'overview',
    mobilePrimary: true,
  },
  {
    id: 'home',
    label: 'Portal home',
    shortLabel: 'Home',
    path: '/portal',
    description: 'Onboarding overview and next steps',
    icon: LayoutDashboard,
    group: 'overview',
    mobilePrimary: true,
  },
  {
    id: 'checklist',
    label: 'Checklist',
    shortLabel: 'Tasks',
    path: '/portal/checklist',
    description: 'Required onboarding tasks',
    icon: ClipboardCheck,
    group: 'overview',
    mobilePrimary: true,
  },
  {
    id: 'learning',
    label: 'Learning hub',
    shortLabel: 'Learn',
    path: '/portal/learning',
    description: 'Courses, pathways, and achievements',
    icon: Sparkles,
    group: 'development',
    mobilePrimary: true,
  },
  {
    id: 'training',
    label: 'Training modules',
    shortLabel: 'Training',
    path: '/portal/training',
    description: 'Assigned training and completion status',
    icon: GraduationCap,
    group: 'development',
  },
  {
    id: 'quality',
    label: 'Quality',
    shortLabel: 'Quality',
    path: '/portal/quality',
    description: 'Evaluations, coaching, and performance',
    icon: ShieldCheck,
    group: 'development',
  },
  {
    id: 'documents',
    label: 'Documents',
    shortLabel: 'Files',
    path: '/portal/documents',
    description: 'Upload, review, and find documents',
    icon: Files,
    group: 'documents',
  },
  {
    id: 'policies',
    label: 'Policies',
    shortLabel: 'Policies',
    path: '/portal/policies',
    description: 'Read and acknowledge policies',
    icon: BookOpenCheck,
    group: 'documents',
  },
  {
    id: 'contracts',
    label: 'Contracts and NDA',
    shortLabel: 'Contracts',
    path: '/portal/contracts-nda',
    description: 'Review agreements and acceptance history',
    icon: FileSignature,
    group: 'documents',
  },
  {
    id: 'connected-tools',
    label: 'Connected tools',
    shortLabel: 'Tools',
    path: '/portal/connected-tools',
    description: 'Open organisation applications',
    icon: Cable,
    group: 'setup',
    activePathPrefixes: ['/portal/apps/'],
  },
  {
    id: 'google-workspace',
    label: 'Google Workspace',
    shortLabel: 'Workspace',
    path: '/portal/google-workspace',
    description: 'Workspace account and application setup',
    icon: BriefcaseBusiness,
    group: 'setup',
  },
  {
    id: 'email-setup',
    label: 'Email setup',
    shortLabel: 'Email',
    path: '/portal/email-setup',
    description: 'Configure email and signature details',
    icon: Mail,
    group: 'setup',
  },
  {
    id: 'welcome-sessions',
    label: 'Welcome sessions',
    shortLabel: 'Sessions',
    path: '/portal/welcome-sessions',
    description: 'Book and manage welcome sessions',
    icon: Video,
    group: 'setup',
  },
  {
    id: 'support',
    label: 'Support',
    shortLabel: 'Help',
    path: '/portal/support',
    description: 'Get help with access or onboarding',
    icon: Headphones,
    group: 'help',
  },
];

const normalizePath = (pathname: string) => (
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
);

export const isPortalNavigationItemActive = (
  item: PortalNavigationItem,
  pathname: string,
): boolean => {
  const normalizedPath = normalizePath(pathname);

  if (item.path === '/portal') {
    return normalizedPath === item.path;
  }

  if (normalizedPath === item.path || normalizedPath.startsWith(`${item.path}/`)) {
    return true;
  }

  return item.activePathPrefixes?.some(prefix => normalizedPath.startsWith(prefix)) ?? false;
};

export const getActivePortalNavigationItem = (pathname: string) => (
  portalNavigationItems.find(item => isPortalNavigationItemActive(item, pathname))
);

export const getPortalPageTitle = (pathname: string): string => {
  if (normalizePath(pathname).startsWith('/portal/apps/')) {
    return 'Connected application';
  }

  return getActivePortalNavigationItem(pathname)?.label ?? 'Employee portal';
};

export const getMobilePortalNavigationItems = () => (
  portalNavigationItems.filter(item => item.mobilePrimary)
);
